import React, { useState, useEffect } from 'react';
import { Card, CardBody, Table, Badge, Button, Input, Row, Col, Spinner, Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import moment from 'moment';
import { toast, Zoom } from 'react-toastify';
import { Search, Eye, Trash2 } from 'react-feather';
import { Get_Meal_Pass_List, Delete_Meal_Pass } from '../../api/MealPassApi/MealPassApi';
import MealPassDetailModal from './MealPassDetailModal';

const MealPassList = ({ refreshSignal, selectedTrustId, trustList }) => {
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Selected pass for modal
  const [selectedPass, setSelectedPass] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchPasses = async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber: page,
        recordsPerPage: pageSize,
        search: search.trim() || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        trustId: selectedTrustId || undefined
      };
      const { data } = await Get_Meal_Pass_List(params);
      if (data?.status === 200 && data.payload?.data) {
        setPasses(data.payload.data);
        setTotalRecords(data.pager?.totalRecords || data.payload.data.length);
      } else {
        setPasses([]);
        setTotalRecords(0);
      }
    } catch (err) {
      console.error('Error fetching meal passes list:', err);
      toast.error('Failed to load meal passes list', { autoClose: 2000, transition: Zoom });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPasses();
  }, [page, pageSize, startDate, endDate, selectedTrustId, refreshSignal]);

  useEffect(() => {
    if (!detailModalOpen) {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    }
  }, [detailModalOpen]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPasses();
  };

  const handleClearFilters = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const handleDelete = async (mealId) => {
    if (!window.confirm(`Are you sure you want to delete / cancel Bhojan Pass #${mealId}?`)) {
      return;
    }
    setDeletingId(mealId);
    try {
      const { data } = await Delete_Meal_Pass(mealId);
      if (data?.status === 200) {
        toast.success('Bhojan Pass deleted successfully', { autoClose: 1500, transition: Zoom });
        fetchPasses();
      } else {
        toast.error(data?.message || 'Failed to delete pass', { autoClose: 2000, transition: Zoom });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error deleting pass', { autoClose: 2000, transition: Zoom });
    } finally {
      setDeletingId(null);
    }
  };

  const openDetail = (pass) => {
    setSelectedPass(pass);
    setDetailModalOpen(true);
  };

  const totalPages = Math.ceil(totalRecords / pageSize) || 1;

  const renderSlotBadges = (scans) => {
    if (!scans || scans.length === 0) return <span className="text-muted">None</span>;
    const types = Array.from(new Set(scans.map((s) => s.mealTimeSlot?.type).filter(Boolean)));
    return (
      <div className="d-flex flex-wrap gap-1">
        {types.map((t) => {
          let label = 'Meal';
          let color = 'primary';
          if (t === 'breakFast') {
            label = 'Breakfast';
            color = 'warning';
          } else if (t === 'lunch') {
            label = 'Lunch';
            color = 'danger';
          } else if (t === 'dinner') {
            label = 'Dinner';
            color = 'info';
          }
          return (
            <Badge key={t} color={color} style={{ fontSize: '10px' }}>
              {label}
            </Badge>
          );
        })}
      </div>
    );
  };

  return (
    <div className="meal-list-wrapper">
      {/* Search & Filter Bar */}
      <Card className="meal-card-kpi mb-3">
        <CardBody className="p-3">
          <form onSubmit={handleSearchSubmit}>
            <Row className="g-2 align-items-center">
              <Col lg="4" md="6" xs="12">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white">
                    <Search size={14} />
                  </span>
                  <Input
                    type="text"
                    placeholder="Search by devotee name or mobile..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </Col>
              <Col lg="3" md="3" xs="6">
                <Input
                  type="date"
                  bsSize="sm"
                  placeholder="From Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Col>
              <Col lg="3" md="3" xs="6">
                <Input
                  type="date"
                  bsSize="sm"
                  placeholder="To Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Col>
              <Col lg="2" md="12" xs="12" className="d-flex gap-2">
                <Button size="sm" color="primary" type="submit" className="w-100">
                  Search
                </Button>
                {(search || startDate || endDate) && (
                  <Button size="sm" color="outline-secondary" onClick={handleClearFilters} type="button">
                    Clear
                  </Button>
                )}
              </Col>
            </Row>
          </form>
        </CardBody>
      </Card>

      {/* Table Card */}
      <Card className="meal-card-kpi">
        <CardBody className="p-0">
          <div className="table-responsive meal-table-scroll">
            <Table hover className="align-middle mb-0" style={{ minWidth: '720px' }}>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '80px' }}>Pass ID</th>
                  <th style={{ minWidth: '130px' }}>Trust</th>
                  <th style={{ minWidth: '160px' }}>Devotee / Guest</th>
                  <th style={{ width: '100px' }}>Diners</th>
                  <th style={{ minWidth: '150px' }}>Validity Period</th>
                  <th style={{ minWidth: '140px' }}>Slots</th>
                  <th style={{ minWidth: '120px' }}>Meals Taken</th>
                  <th style={{ minWidth: '100px' }}>Issued On</th>
                  <th style={{ width: '110px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5">
                      <Spinner color="primary" size="sm" />
                      <span className="ms-2 text-muted">Loading meal passes...</span>
                    </td>
                  </tr>
                ) : passes.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5 text-muted">
                      No meal passes found. Click <strong>"Issue New Meal Pass"</strong> to create one.
                    </td>
                  </tr>
                ) : (
                  passes.map((item) => {
                    const totalSlots = item.mealPassScans?.length || 0;
                    const takenSlots = item.mealPassScans?.filter((s) => s.isMealTaken)?.length || 0;
                    const isAllDone = totalSlots > 0 && takenSlots === totalSlots;

                    return (
                      <tr key={item.mealId}>
                        <td>
                          <strong>#{item.mealId}</strong>
                        </td>
                        <td>
                          <Badge color="light" className="text-dark border" style={{ fontSize: '11px', whiteSpace: 'normal', textAlign: 'left' }}>
                            {item.trustDetail?.trustName || 'Default Trust'}
                          </Badge>
                        </td>
                        <td>
                          <div className="fw-bold text-dark">{item.personDetail?.name || '--'}</div>
                          {item.personDetail?.mobile && (
                            <div className="text-muted small" style={{ fontSize: '11px' }}>
                              📱 {item.personDetail.mobile}
                            </div>
                          )}
                          {item.personDetail?.city && (
                            <div className="text-muted small" style={{ fontSize: '11px' }}>
                              📍 {item.personDetail.city}
                            </div>
                          )}
                        </td>
                        <td>
                          <Badge color="info" className="px-2 py-1">
                            {item.person} Person{item.person > 1 ? 's' : ''}
                          </Badge>
                        </td>
                        <td style={{ fontSize: '12px' }}>
                          <div>{moment(item.startDate).format('DD MMM YYYY')}</div>
                          <div className="text-muted">to {moment(item.endDate).format('DD MMM YYYY')}</div>
                        </td>
                        <td>{renderSlotBadges(item.mealPassScans)}</td>
                        <td>
                          <Badge color={isAllDone ? 'secondary' : takenSlots > 0 ? 'success' : 'warning'}>
                            {takenSlots} / {totalSlots} Served
                          </Badge>
                        </td>
                        <td style={{ fontSize: '12px' }}>
                          {moment(item.createdAt).format('DD/MM/YY HH:mm')}
                        </td>
                        <td>
                          <div className="d-flex justify-content-center gap-1">
                            <Button
                              size="sm"
                              color="outline-primary"
                              className="px-2"
                              title="View Ticket & QR Code"
                              onClick={() => openDetail(item)}
                            >
                              <Eye size={13} />
                            </Button>
                            <Button
                              size="sm"
                              color="outline-danger"
                              className="px-2"
                              title="Cancel / Delete Pass"
                              disabled={deletingId === item.mealId}
                              onClick={() => handleDelete(item.mealId)}
                            >
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination Footer */}
          {!loading && totalRecords > 0 && (
            <div className="d-flex align-items-center justify-content-between p-3 border-top flex-wrap gap-2">
              <span className="text-muted small">
                Showing <strong>{Math.min(totalRecords, (page - 1) * pageSize + 1)}</strong> to{' '}
                <strong>{Math.min(totalRecords, page * pageSize)}</strong> of <strong>{totalRecords}</strong> passes
              </span>
              <Pagination size="sm" className="mb-0">
                <PaginationItem disabled={page <= 1}>
                  <PaginationLink previous onClick={() => setPage((p) => Math.max(1, p - 1))} />
                </PaginationItem>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pNum = i + 1;
                  return (
                    <PaginationItem active={page === pNum} key={pNum}>
                      <PaginationLink onClick={() => setPage(pNum)}>{pNum}</PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem disabled={page >= totalPages}>
                  <PaginationLink next onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
                </PaginationItem>
              </Pagination>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Ticket Details Modal */}
      <MealPassDetailModal
        isOpen={detailModalOpen}
        toggle={() => setDetailModalOpen(!detailModalOpen)}
        mealPass={selectedPass}
      />
    </div>
  );
};

export default MealPassList;
